import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ApiPageAdapter,
  LocationId,
  Page,
  PagedApiRequest,
  PagedApiResponse,
} from '@fmnts/api/shared';
import { ApiConfigService, ApiRequestHelper } from '@fmnts/api/util';
import { hasProperty, isString } from '@fmnts/core';
import { EMPTY, Observable, expand, map, reduce } from 'rxjs';
import { ApiNextLocationAdapter } from '../adapter/next-location.adapter';
import {
  IApiNextLocation,
  IApiNextLocationCheckInCreateDto,
} from '../api-model';
import { NextLocation, NextLocationCheckIn } from '../model/next-location';

export declare type GetNextLocationListQuery = PagedApiRequest;

const hasNextPage = hasProperty('next', isString);

@Injectable({ providedIn: 'root' })
export class LocationNextApi {
  private static readonly rootUrl = '/api/next/locations';

  private readonly _fromApiAdapter = new ApiNextLocationAdapter();
  private readonly _fromApiPageAdapter = new ApiPageAdapter(
    this._fromApiAdapter,
  );

  constructor(
    private apiHelper: ApiRequestHelper,
    private configService: ApiConfigService,
    private http: HttpClient,
  ) {}

  public get(id: LocationId): Observable<NextLocation> {
    const url = this.configService.buildApiUrl([LocationNextApi.rootUrl, id]);
    const headers = this.apiHelper.makeDefaultHeaders({});

    return this.http
      .get<IApiNextLocation>(url, { headers })
      .pipe(map((response) => this._fromApiAdapter.adapt(response)));
  }

  /**
   * List of locations assigned for the current team.
   *
   * @returns
   * An `Observable` that emits a paginated list of locations.
   *
   */
  list(query: GetNextLocationListQuery = {}): Observable<Page<NextLocation>> {
    const url = this.configService.buildApiUrl([LocationNextApi.rootUrl]);
    const headers = this.apiHelper.makeDefaultHeaders({});
    const params = this.apiHelper.makeParams(query);

    return this.http
      .get<PagedApiResponse<IApiNextLocation>>(url, { headers, params })
      .pipe(map((response) => this._fromApiPageAdapter.adapt(response)));
  }

  /**
   * List of all locations assigned for the current team.
   * Calls the `list` endpoint, until all pages are fetched
   *
   * @param query
   * @returns
   * An `Observable` containing an array of all locations
   *
   */
  listAll(query: GetNextLocationListQuery = {}): Observable<NextLocation[]> {
    return this.list(query).pipe(
      expand((response) =>
        hasNextPage(response)
          ? this.list({
              ...query,
              page: response.currentPage + 1,
            })
          : EMPTY,
      ),
      reduce(
        (acc, current) => acc.concat(current.results),
        [] as NextLocation[],
      ),
    );
  }

  /**
   * Creates a new check in for the current user.
   * @param checkIn
   * @returns
   */
  checkIn(
    checkIn: IApiNextLocationCheckInCreateDto,
  ): Observable<NextLocationCheckIn> {
    const url = this.configService.buildApiUrl([
      LocationNextApi.rootUrl,
      'checkin',
    ]);

    return this.http.post<NextLocationCheckIn>(url, checkIn);
  }
}
